import { ConnectionListResult, FeatureFlagsContext, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { FlowsDisplayGridRowDataFragment } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragment';
import { FlowsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  UpstreamFlowsFilteredByFieldQuery,
  UpstreamFlowsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/UpstreamFlowsFilteredByFieldQuery';
import {
  UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQuery,
  UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQuery';
import { WorkbookUpstreamFlowsQuery, WorkbookUpstreamFlowsQueryVariables } from '../../../gen-src/queries/WorkbookUpstreamFlowsQuery';
import { WorkbookUpstreamFlowsWithoutPersonalSpaceQuery, WorkbookUpstreamFlowsWithoutPersonalSpaceQueryVariables } from '../../../gen-src/queries/WorkbookUpstreamFlowsWithoutPersonalSpaceQuery';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import { UpstreamFlowsFilteredByFieldQueryGql } from '../../queries/UpstreamFlowsFilteredByFieldQueryGql';
import { UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryGql } from '../../queries/UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryGql';
import { WorkbookUpstreamFlowsQueryGql } from '../../queries/WorkbookUpstreamFlowsQueryGql';
import { WorkbookUpstreamFlowsWithoutPersonalSpaceQueryGql } from '../../queries/WorkbookUpstreamFlowsWithoutPersonalSpaceQueryGql';
import {
  FlowsDisplayGrid,
  FlowsDisplayGridRowData,
  getRowDataForFlow,
  getRowDataForFlowWithoutPersonalSpace,
} from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = WorkbookUpstreamFlowsQueryVariables;
type QueryResults = WorkbookUpstreamFlowsQuery;
type QueryResultListNode = FlowsDisplayGridRowDataFragment;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = WorkbookUpstreamFlowsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = WorkbookUpstreamFlowsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = UpstreamFlowsFilteredByFieldQueryVariables;
type QueryResultsWithFilter = UpstreamFlowsFilteredByFieldQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.workbooks && resultData.workbooks[0] && resultData.workbooks[0].upstreamFlowsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.workbooks && resultData.workbooks[0] && resultData.workbooks[0].upstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter): ConnectionListResult<QueryResultListNode> | null {
  return (
    resultData &&
    resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] &&
    resultData.fieldsConnection.nodes[0].upstreamFlowsConnection
  );
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return (
    resultData &&
    resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] &&
    resultData.fieldsConnection.nodes[0].upstreamFlowsConnection
  );
}

export type WorkbookUpstreamFlowsQueryGridProps = GridSortProps<FlowsValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredFieldId: string | null;
  readonly rowCount: number | null;
};

export function WorkbookUpstreamFlowsQueryGrid(props: WorkbookUpstreamFlowsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredFlowsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFieldId ? (
      // Query with Selected Field as Filter
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={UpstreamFlowsFilteredByFieldQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={WorkbookUpstreamFlowsQueryGql}
        partialQueryVariables={{ workbookVizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={UpstreamFlowsFilteredByFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={WorkbookUpstreamFlowsWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ workbookVizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  );
}
